import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import React from 'react'

import SearchableSelect from '@/app/component/atom/form/searchable-select'
import Loading from '@/app/component/guard/loading'
import { useGetCampaignOptions } from '@/app/module/campaigns/hooks/use-get-campaign-options'
import { BackendCampaignTemplate } from '@/app/module/campaigns/types'

type Props = {
  campaignTypes?: BackendCampaignTemplate[]
  selectedCampaignName?: string
  value: string | number

  onChange: (campaignId: string, campaignName: string) => void
}

const CampaignSelect: React.FC<Props> = ({ campaignTypes = [], selectedCampaignName, value, onChange }) => {
  const [showArchived, setShowArchived] = React.useState(false)
  const { data: options, isFetching } = useGetCampaignOptions({
    page: 1,
    size: 100,
    aborted: false,
    archived: showArchived,
    templates: campaignTypes.length ? campaignTypes.join(',') : undefined,
  })
  const campaignId = String(value)
  const campaignName = React.useMemo(
    () => options.find((option) => String(option.value) === campaignId)?.label,
    [campaignId, options],
  )
  const campaignOptions = React.useMemo(() => {
    const formatted = options.map((option) => ({ ...option, value: String(option.value) }))

    if (value && !formatted.some((option) => option.value === campaignId)) {
      formatted.unshift({ label: selectedCampaignName || `Campaign ${campaignId}`, value: campaignId })
    }

    return formatted
  }, [campaignId, options, selectedCampaignName, value])

  React.useEffect(() => {
    if (value && selectedCampaignName !== undefined && campaignName && campaignName !== selectedCampaignName) {
      onChange(campaignId, campaignName) // update campaign name if mismatched (action campaign name is a snapshot and can be outdated)
    }
  }, [campaignId, campaignName, selectedCampaignName, value, onChange])

  return (
    <Loading isLoading={isFetching}>
      <SearchableSelect
        hideRemoveSelection
        helperText="Only the 100 most recent campaigns are shown."
        id="select-campaign"
        keyPropFn={(option) => option.value}
        label="Select Campaign"
        options={campaignOptions}
        showAll
        value={campaignId}
        valuePropFn={(option) => option.label}
        onChange={(selectedId) => {
          const selectedOption = campaignOptions.find((option) => option.value === selectedId)
          if (selectedOption) {
            onChange(selectedId, selectedOption.label)
          }
        }}
      />
      <FormControlLabel
        control={
          <Switch
            checked={showArchived}
            name="show-archived"
            slotProps={{ input: { role: 'switch' } }}
            onChange={(_, checked) => setShowArchived(checked)}
          />
        }
        label="Show archived"
      />
    </Loading>
  )
}

export default CampaignSelect
